/* Shared card mapper for ListView. Standard video content types (Movie,
   Podcast, Short Clip, Live Stream, Live Channel) all expose the same record
   shape — { id, metadata_id, name, media, status, rating, access } — so they
   reuse this factory instead of each duplicating a getCard.

   handlers receive (id, metadata_id, item); types that don't need `item`
   (most) simply ignore the third argument. */

export const statusTone = (status = "") => {
  const s = String(status).toLowerCase();
  if (s.includes("publish") || s.includes("approved")) return "success";
  if (s.includes("draft") || s.includes("pending")) return "warning";
  return "neutral";
};

/* `status` is the author's publish intent (Publish / Save Draft); `qc_status`
   is the review outcome (Approved / Pending / Rejected). Content set to
   Publish is only actually live once QC has approved it. Records from
   endpoints that don't send qc_status are treated as approved (unchanged). */
const isPubStatus = (s = "") => /publish|approved/i.test(s);
const isDraftStatus = (s = "") => /draft|pending/i.test(s);
const isQcApproved = (qc) => !qc || /approved/i.test(qc);
const isInReview = (d) => isPubStatus(d.status) && !isQcApproved(d.qc_status);

/* Stat tiles shared by content listings. Total is the global count from the
   API; the rest are derived from the currently loaded page (labelled as such)
   until an aggregate endpoint exists. */
export const contentStats = (data = [], totalCount = 0) => {
  const stats = [
    { label: "Total", value: (totalCount || 0).toLocaleString() },
    {
      label: "Published",
      value: data.filter((d) => isPubStatus(d.status) && isQcApproved(d.qc_status))
        .length,
      sub: "on this page",
      accent: "var(--state-success, #5dcaa5)",
    },
    {
      label: "Drafts",
      value: data.filter((d) => isDraftStatus(d.status)).length,
      sub: "on this page",
      accent: "var(--state-warning, #e0b341)",
    },
  ];
  // Only listings that carry qc_status get the review tile.
  if (data.some((d) => d.qc_status)) {
    stats.push({
      label: "In Review",
      value: data.filter(isInReview).length,
      sub: "on this page",
      accent: "var(--state-warning, #e0b341)",
    });
  }
  return stats;
};

/* Badge shown on a content card — a Publish item still awaiting QC reads as
   its review state instead of "Publish". */
export const contentBadge = (item) => {
  if (isInReview(item)) {
    const label = /reject/i.test(item.qc_status) ? "Rejected" : "In Review";
    return { label, tone: label === "Rejected" ? "neutral" : "warning" };
  }
  return item.status ? { label: item.status, tone: statusTone(item.status) } : null;
};

// The badge a creator actually needs to see. QC outcome outranks the
// publish status: content that was rejected, or is still awaiting review,
// is NOT live no matter what `status` says — showing a green "Publish"
// badge on rejected content is how a creator misses that anything is wrong.
export const qcBadge = (item) => {
  const qc = String(item.qc_status || "").trim().toLowerCase();
  if (qc === "rejected") return { label: "Rejected", tone: "danger" };
  if (qc === "pending" && /publish/i.test(item.status || ""))
    return { label: "In review", tone: "warning" };
  return item.status
    ? { label: item.status, tone: statusTone(item.status) }
    : null;
};

export const buildContentCard =
  (handleEdit, handleDelete, deletingId) => (item) => {
    const metaBits = [item.rating, item.access].filter(
      (v) => v && v !== "N/A"
    );
    const delId = item.metadata_id || item.id;
    return {
      id: delId,
      title: item.name,
      image: item.media,
      meta: metaBits.join(" · "),
      badge: contentBadge(item),
      onClick: () => handleEdit(item.id, item.metadata_id, item),
      actions: [
        {
          icon: "edit",
          label: "Edit",
          onClick: () => handleEdit(item.id, item.metadata_id, item),
        },
        {
          icon: "delete",
          label: "Delete",
          disabled: deletingId === delId,
          onClick: () => handleDelete(item.id, item.metadata_id, item),
        },
      ],
    };
  };
